import type { AIResourcesQueryRequest, AIResourcesQueryResult, AIResourcesService } from '../core/types';

interface CreateFirestoreAIResourcesServiceOptions {
  region?: string;
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function debugLog(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  const flag = (window as { __LECTURA_DEBUG_AI__?: boolean }).__LECTURA_DEBUG_AI__ ?? true;
  if (!flag) return;
  console.debug('[Lectura AI]', ...args);
}

function getFunctionsCompat(region = 'us-central1'): {
  httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }>;
} {
  const fb = typeof window !== 'undefined' ? (window as { firebase?: unknown }).firebase : null;
  if (!fb || typeof (fb as { functions?: unknown }).functions !== 'function') {
    throw new Error('Firebase Functions is not available');
  }
  const fns = (fb as {
    functions: (() => { httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }> }) & {
      region?: (region: string) => { httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }>;
      };
    };
  }).functions;
  return typeof fns.region === 'function' ? fns.region(region) : fns();
}

export function createFirestoreAIResourcesService(
  opts: CreateFirestoreAIResourcesServiceOptions = {}
): AIResourcesService {
  const region = String(opts.region || 'us-central1');
  return {
    async queryPresentation(presId: string, request: AIResourcesQueryRequest): Promise<AIResourcesQueryResult> {
      const svc = getFunctionsCompat(region);
      const call = svc.httpsCallable('queryPresentationAI');
      debugLog('queryPresentationAI:request', {
        presId,
        hasChatContext: !!request.chatContext,
        slideIndex: request.slideIndex,
        topK: request.topK
      });
      const response = await call({
        presentationId: String(presId || '').trim(),
        question: String(request.question || '').trim(),
        topK: Number(request.topK || 6),
        ...(request.chatContext ? { chatContext: String(request.chatContext || '').slice(0, 4000) } : {}),
        ...(typeof request.slideIndex === 'number' ? { slideIndex: request.slideIndex } : {})
      });
      const data = (response && typeof response === 'object' ? (response as { data?: unknown }).data : null) as
        | AIResourcesQueryResult
        | null;
      if (!data || typeof data !== 'object') throw new Error('AI query returned no data');
      const result: AIResourcesQueryResult = {
        answer: String((data as { answer?: unknown }).answer || ''),
        citations: Array.isArray((data as { citations?: unknown }).citations)
          ? ((data as { citations: unknown[] }).citations as AIResourcesQueryResult['citations'])
          : [],
        model: typeof (data as { model?: unknown }).model === 'string'
          ? (data as { model?: string }).model
          : undefined
      };
      debugLog('queryPresentationAI:response', { presId, result });
      return result;
    },
    async requestReindex(
      presId: string,
      options?: { resourceId?: string; slidesText?: string }
    ): Promise<{
      ok: boolean;
      count?: number;
      indexed?: number;
      failed?: number;
      errors?: Array<{ resourceId: string; error: string }>;
    }> {
      try {
        const svc = getFunctionsCompat(region);
        const call = svc.httpsCallable('requestResourcesReindex');
        const resourceId = typeof options === 'string' ? options : options?.resourceId;
        const slidesText = (typeof options === 'object' && options ? String(options.slidesText || '') : '').trim();
        debugLog('requestResourcesReindex:request', { presId, resourceId, hasSlidesText: !!slidesText });
        const response = await call({
          presentationId: String(presId || '').trim(),
          ...(resourceId ? { resourceId: String(resourceId).trim() } : {}),
          ...(slidesText ? { slidesText } : {})
        });
        const data = (response && typeof response === 'object' ? (response as { data?: unknown }).data : null) as
          | {
              ok?: unknown;
              count?: unknown;
              indexed?: unknown;
              failed?: unknown;
              errors?: unknown;
            }
          | null;
        const result = {
          ok: data?.ok === true,
          ...(typeof data?.count === 'number' ? { count: data.count } : {}),
          ...(typeof data?.indexed === 'number' ? { indexed: data.indexed } : {}),
          ...(typeof data?.failed === 'number' ? { failed: data.failed } : {}),
          ...(Array.isArray(data?.errors)
            ? { errors: data.errors as Array<{ resourceId: string; error: string }> }
            : {})
        };
        debugLog('requestResourcesReindex:response', { presId, resourceId, result });
        return result;
      } catch (err) {
        debugLog('requestResourcesReindex:error', toErrorMessage(err, 'Failed to request reindex'));
        throw new Error(toErrorMessage(err, 'Failed to request reindex'));
      }
    },
    async getQuotaSnapshot(presId: string): Promise<{
      day: string;
      sync: { used: number; limit: number };
      query: { used: number; limit: number };
      userProfile?: {
        day: string;
        viewer: { used: number; limit: number };
        presenter: { used: number; limit: number };
      };
    }> {
      const svc = getFunctionsCompat(region);
      const call = svc.httpsCallable('getAIPresentationQuota');
      debugLog('getAIPresentationQuota:request', { presId });
      const response = await call({
        presentationId: String(presId || '').trim()
      });
      const data = (response && typeof response === 'object' ? (response as { data?: unknown }).data : null) as
        | {
            day?: unknown;
            sync?: { used?: unknown; limit?: unknown };
            query?: { used?: unknown; limit?: unknown };
            presentation?: unknown;
            userProfile?: {
              day?: unknown;
              viewer?: { used?: unknown; limit?: unknown };
              presenter?: { used?: unknown; limit?: unknown };
            };
          }
        | null;

      const parseUserProfile = (): {
        day: string;
        viewer: { used: number; limit: number };
        presenter: { used: number; limit: number };
      } | undefined => {
        const up = data?.userProfile;
        if (!up || typeof up !== 'object') return undefined;
        return {
          day: String((up as { day?: unknown }).day || ''),
          viewer: {
            used: Number((up as { viewer?: { used?: unknown } }).viewer?.used || 0),
            limit: Number((up as { viewer?: { limit?: unknown } }).viewer?.limit || 0)
          },
          presenter: {
            used: Number((up as { presenter?: { used?: unknown } }).presenter?.used || 0),
            limit: Number((up as { presenter?: { limit?: unknown } }).presenter?.limit || 0)
          }
        };
      };

      const userProfile = parseUserProfile();

      if (data && data.presentation === null) {
        const result = {
          day: userProfile?.day || '',
          sync: { used: 0, limit: 0 },
          query: { used: 0, limit: 0 },
          ...(userProfile ? { userProfile } : {})
        };
        debugLog('getAIPresentationQuota:response', { presId, result });
        return result;
      }

      const result = {
        day: String(data?.day || ''),
        sync: {
          used: Number((data?.sync as { used?: unknown } | undefined)?.used || 0),
          limit: Number((data?.sync as { limit?: unknown } | undefined)?.limit || 0)
        },
        query: {
          used: Number((data?.query as { used?: unknown } | undefined)?.used || 0),
          limit: Number((data?.query as { limit?: unknown } | undefined)?.limit || 0)
        },
        ...(userProfile ? { userProfile } : {})
      };
      debugLog('getAIPresentationQuota:response', { presId, result });
      return result;
    }
  };
}

export const aiResourcesServiceStub: AIResourcesService = {
  async queryPresentation() {
    return {
      answer: 'AI resources API is not configured for this environment.',
      citations: []
    };
  },
  async requestReindex() {
    return { ok: true };
  },
  async getQuotaSnapshot() {
    return {
      day: '',
      sync: { used: 0, limit: 0 },
      query: { used: 0, limit: 0 },
      userProfile: {
        day: '',
        viewer: { used: 0, limit: 0 },
        presenter: { used: 0, limit: 0 }
      }
    };
  }
};
